import { TextSelection, } from "prosemirror-state";
export const handleEnterOnHeading = (schema) => {
    return (state, dispatch) => {
        const { $from } = state.selection;
        const headingType = schema.nodes.heading;
        const paragraphType = schema.nodes.paragraph;
        // Locate the heading where the cursor resides
        const headingNode = findHeadingFromDocument(state.doc, $from, headingType);
        if (!headingNode) {
            return false; // Fallback to default behavior if no heading is found
        }
        const { node, pos } = headingNode;
        if (!node || pos < 0 || pos >= state.doc.content.size) {
            return false; // Fail gracefully
        }
        if (node.attrs.collapse) {
            // Case 1: Heading is collapsed, expand it
            if (dispatch) {
                try {
                    const tr = state.tr.setNodeMarkup(pos, undefined, {
                        ...node.attrs,
                        collapse: false, // Expand the heading
                    });
                    // Insert a new paragraph inside the expanded heading
                    const insertPos = pos + node.nodeSize; // After the heading content
                    if (insertPos >= 0 && insertPos <= state.doc.content.size) {
                        tr.insert(insertPos, paragraphType.create());
                        // Move the cursor to the new paragraph
                        dispatch(tr.setSelection(TextSelection.near(tr.doc.resolve(insertPos))));
                    }
                }
                catch (error) {
                    return false;
                }
            }
            return true;
        }
        else {
            // Case 2: Heading is expanded, insert a paragraph after it
            return false; // Default behavior
        }
    };
};
/**
 * Finds the closest heading node from the current selection position.
 *
 * Handles cases where the document might only have one heading.
 *
 * @param doc - ProseMirror document node
 * @param $from - Current resolved position
 * @param headingType - Heading node type
 * @returns The heading node and its position, or null if not found
 */
function findHeadingFromDocument(doc, $from, headingType) {
    for (let depth = $from.depth; depth >= 0; depth--) {
        const node = $from.node(depth);
        const pos = depth > 0 ? $from.before(depth) : 0;
        if (node.type === headingType) {
            if (node.attrs.collapse) {
                // If the heading is collapsed, treat the cursor as being part of the heading
                if ($from.pos >= pos && $from.pos < pos + node.nodeSize) {
                    return { node, pos };
                }
            }
            else {
                // If the heading is expanded, ensure the cursor is logically within the heading's bounds
                if ($from.pos >= pos && $from.pos <= pos + node.nodeSize) {
                    return { node, pos };
                }
            }
        }
    }
    // Fallback: Handle the last heading specifically
    let lastHeading = null;
    doc.descendants((node, pos) => {
        if (node.type === headingType) {
            lastHeading = { node, pos };
        }
    });
    // Check if the cursor is beyond the last heading
    if (lastHeading && $from.pos >= lastHeading.pos + lastHeading.node.nodeSize) {
        return lastHeading;
    }
    return null; // No heading found
}
/**
 * Inserts a paragraph after the current heading node.
 */
export function insertParagraphAfterHeading(schema) {
    return (state, dispatch) => {
        const { paragraph, heading } = schema.nodes;
        if (!paragraph || !heading)
            return false;
        const { $from } = state.selection;
        // Ensure we are in a heading node
        if ($from.parent.type === heading) {
            const headingPos = $from.before($from.depth); // Start of heading node
            const headingNodeSize = $from.parent.nodeSize;
            const afterHeadingPos = headingPos + headingNodeSize; // Position right after heading
            if (dispatch) {
                const tr = state.tr.insert(afterHeadingPos, paragraph.create());
                // Use the resolved position in the updated document
                const resolvedPos = tr.doc.resolve(afterHeadingPos);
                tr.setSelection(TextSelection.near(resolvedPos));
                dispatch(tr.scrollIntoView());
            }
            return true;
        }
        return false;
    };
}
